import React, { useState, useContext } from 'react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHandshake, faSave } from '@fortawesome/free-solid-svg-icons'
import Swal from 'sweetalert2'
import AuthContext from '../../middlewares/AuthContext'

const url = process.env.NODE_ENV === 'production'
  ? process.env.REACT_APP_URL
  : process.env.REACT_APP_URL_DEV

const BRAND      = '#6eb446'
const BRAND_DARK = '#2d5918'

interface AuthCtx {
  getHeaders: () => Promise<Record<string, string>>
}

interface PartnerForm {
  name                  : string
  description           : string
  street_address        : string
  address_line_2        : string
  city                  : string
  zip_code              : string
  state                 : string
  country               : string
  invoice_street_address: string
  invoice_address_line_2: string
  invoice_city          : string
  invoice_zip_code      : string
  invoice_state         : string
  invoice_country       : string
  invoice_email         : string
  discount              : number
  discount_smp          : number
}

const INITIAL: PartnerForm = {
  name                  : '',
  description           : '',
  street_address        : '',
  address_line_2        : '',
  city                  : '',
  zip_code              : '',
  state                 : '',
  country               : '',
  invoice_street_address: '',
  invoice_address_line_2: '',
  invoice_city          : '',
  invoice_zip_code      : '',
  invoice_state         : '',
  invoice_country       : '',
  invoice_email         : '',
  discount              : 0,
  discount_smp          : 0,
}

const formSchema = Yup.object().shape({
  name                  : Yup.string().min(3).max(255).trim().required('Required'),
  description           : Yup.string().min(3).max(255).trim().required('Required'),
  street_address        : Yup.string().min(3).max(255).trim().required('Required'),
  address_line_2        : Yup.string().max(255).trim(),
  city                  : Yup.string().min(3).max(255).trim().required('Required'),
  zip_code              : Yup.string().matches(/^[0-9]+$/, 'Digits only').min(3).max(10).required('Required'),
  state                 : Yup.string().min(3).max(255).trim().required('Required'),
  country               : Yup.string().min(3).max(255).trim().required('Required'),
  invoice_street_address: Yup.string().min(3).max(255).trim().required('Required'),
  invoice_address_line_2: Yup.string().max(255).trim(),
  invoice_city          : Yup.string().min(3).max(255).trim().required('Required'),
  invoice_zip_code      : Yup.string().matches(/^[0-9]+$/, 'Digits only').min(3).max(10).required('Required'),
  invoice_state         : Yup.string().min(3).max(255).trim().required('Required'),
  invoice_country       : Yup.string().min(3).max(255).trim().required('Required'),
  invoice_email         : Yup.string().trim().email().required('Required'),
  discount              : Yup.number().typeError('Must be a number').min(1).max(99).required('Required'),
  discount_smp          : Yup.number().typeError('Must be a number').min(0).max(99).required('Required'),
})

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label style={{ display: 'block', marginBottom: '0.4rem', fontSize: '0.78rem', fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
    {children}
  </label>
)

const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p style={{ fontWeight: 800, color: '#1e293b', fontSize: '0.82rem', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '1rem' }}>
    {children}
  </p>
)

const CreatePartner: React.FC = () => {
  const { getHeaders } = useContext(AuthContext) as AuthCtx
  const { t }          = useTranslation()

  const [loading, setLoading] = useState(false)

  const handleSubmit = async (values: PartnerForm, { resetForm }: { resetForm: () => void }) => {
    setLoading(true)
    try {
      const headers = await getHeaders()
      const res     = await axios.post<{ response: string }>(`${url}partners`, values, { headers })
      Swal.fire({ icon: 'success', title: res.data.response })
      resetForm()
    } catch (err: any) {
      Swal.fire({
        icon : 'error',
        title: err?.response?.data?.response || 'Error',
        text : err?.response?.data?.message,
      })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="row justify-content-center">
      <div className="col-lg-11">

        {/* Header card */}
        <div style={{
          backgroundColor: '#fff',
          borderRadius   : 16,
          boxShadow      : '0 4px 20px rgba(0,0,0,0.07)',
          border         : '1px solid #f1f5f9',
          overflow       : 'hidden',
          marginBottom   : '1.5rem',
        }}>
          <div style={{ height: 4, background: `linear-gradient(90deg, ${BRAND_DARK}, ${BRAND})` }} />
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10, backgroundColor: `${BRAND}18`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <FontAwesomeIcon icon={faHandshake} style={{ color: BRAND, fontSize: '1.1rem' }} />
            </div>
            <p style={{ margin: 0, fontSize: '1rem', fontWeight: 800, color: '#1e293b' }}>
              {t('partners.create.partner_creation')}
            </p>
          </div>
        </div>

        <Formik initialValues={INITIAL} validationSchema={formSchema} onSubmit={handleSubmit}>
          <Form>
            <div className="row g-4">

              {/* Name / Description / Discounts */}
              <div className="col-12">
                <div style={{
                  backgroundColor: '#fff', borderRadius: 16,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden',
                }}>
                  <div style={{ height: 3, backgroundColor: BRAND }} />
                  <div style={{ padding: '1.5rem' }}>
                    <div className="row g-3">
                      <div className="col-md-4">
                        <FieldLabel>{t('partners.name')} *</FieldLabel>
                        <Field type="text" name="name" className="form-control form-control-sm" />
                        <ErrorMessage name="name" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="col-md-4">
                        <FieldLabel>{t('partners.create.description')} *</FieldLabel>
                        <Field as="textarea" name="description" className="form-control form-control-sm" style={{ height: 70 }} />
                        <ErrorMessage name="description" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="col-md-2">
                        <FieldLabel>{t('partners.create.discount')} *</FieldLabel>
                        <Field type="text" name="discount" className="form-control form-control-sm" />
                        <ErrorMessage name="discount" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                      <div className="col-md-2">
                        <FieldLabel>Descuento SMP *</FieldLabel>
                        <Field type="text" name="discount_smp" className="form-control form-control-sm" />
                        <ErrorMessage name="discount_smp" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Physical Address */}
              <div className="col-md-6">
                <div style={{
                  backgroundColor: '#fff', borderRadius: 16,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden', height: '100%',
                }}>
                  <div style={{ height: 3, backgroundColor: BRAND }} />
                  <div style={{ padding: '1.5rem' }}>
                    <SectionTitle>{t('partners.address')}</SectionTitle>

                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.street_address')} *</FieldLabel>
                      <Field type="text" name="street_address" className="form-control form-control-sm" />
                      <ErrorMessage name="street_address" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.address_line')}</FieldLabel>
                      <Field type="text" name="address_line_2" className="form-control form-control-sm" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.city')} *</FieldLabel>
                      <Field type="text" name="city" className="form-control form-control-sm" />
                      <ErrorMessage name="city" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.zip')} *</FieldLabel>
                      <Field type="text" name="zip_code" className="form-control form-control-sm" />
                      <ErrorMessage name="zip_code" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.state')} *</FieldLabel>
                      <Field type="text" name="state" className="form-control form-control-sm" />
                      <ErrorMessage name="state" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.country')} *</FieldLabel>
                      <Field type="text" name="country" className="form-control form-control-sm" />
                      <ErrorMessage name="country" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Address */}
              <div className="col-md-6">
                <div style={{
                  backgroundColor: '#fff', borderRadius: 16,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.07)', border: '1px solid #f1f5f9', overflow: 'hidden', height: '100%',
                }}>
                  <div style={{ height: 3, backgroundColor: '#3b82f6' }} />
                  <div style={{ padding: '1.5rem' }}>
                    <SectionTitle>{t('partners.create.street_address_inv')}</SectionTitle>

                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.street_address_inv')} *</FieldLabel>
                      <Field type="text" name="invoice_street_address" className="form-control form-control-sm" />
                      <ErrorMessage name="invoice_street_address" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.address_line_inv')}</FieldLabel>
                      <Field type="text" name="invoice_address_line_2" className="form-control form-control-sm" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.city_inv')} *</FieldLabel>
                      <Field type="text" name="invoice_city" className="form-control form-control-sm" />
                      <ErrorMessage name="invoice_city" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.zip')} *</FieldLabel>
                      <Field type="text" name="invoice_zip_code" className="form-control form-control-sm" />
                      <ErrorMessage name="invoice_zip_code" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.state_inv')} *</FieldLabel>
                      <Field type="text" name="invoice_state" className="form-control form-control-sm" />
                      <ErrorMessage name="invoice_state" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.country_inv')} *</FieldLabel>
                      <Field type="text" name="invoice_country" className="form-control form-control-sm" />
                      <ErrorMessage name="invoice_country" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                    <div className="mb-3">
                      <FieldLabel>{t('partners.create.email_inv')} *</FieldLabel>
                      <Field type="email" name="invoice_email" className="form-control form-control-sm" />
                      <ErrorMessage name="invoice_email" component="p" className="text-danger small fw-bold mt-1 mb-0" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="col-12">
                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    display        : 'inline-flex',
                    alignItems     : 'center',
                    justifyContent : 'center',
                    gap            : '0.5rem',
                    width          : '100%',
                    padding        : '0.55rem 1.5rem',
                    borderRadius   : 9999,
                    backgroundColor: loading ? '#94a3b8' : BRAND,
                    color          : '#fff',
                    fontWeight     : 700,
                    fontSize       : '0.875rem',
                    border         : 'none',
                    cursor         : loading ? 'not-allowed' : 'pointer',
                    boxShadow      : loading ? 'none' : `0 4px 14px ${BRAND}55`,
                    transition     : 'filter 0.15s, transform 0.15s',
                  }}
                  onMouseEnter={e => { if (!loading) { e.currentTarget.style.filter = 'brightness(1.09)'; e.currentTarget.style.transform = 'translateY(-1px)' }}}
                  onMouseLeave={e => { e.currentTarget.style.filter = 'brightness(1)'; e.currentTarget.style.transform = 'translateY(0)' }}
                >
                  {loading
                    ? <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    : <FontAwesomeIcon icon={faSave} />
                  }
                  <span>{t('partners.create.create_partner')}</span>
                </button>
              </div>

            </div>
          </Form>
        </Formik>

      </div>
    </div>
  )
}

export default CreatePartner
